import { useCallback } from 'react';

import { useAppStore, getAppStore } from '@/store';
import pollUntilCompleted from '@/requests/magic-flow/pollUntilCompleted';
import { clearGeneration } from '@/utils/storage/generationStorage';
import { MAGIC_FLOW_WEBHOOK_URL } from '@/consts/urls';
import buildFlowMedia from '@/utils/generation/buildFlowMedia';
import renameDriveFile from '@/utils/generation/renameDriveFile';
import decidePostGenerationUX from '@/utils/generation/decidePostGenerationUX';

import type { IAppStore } from '@/store';
import type { FlowMedia } from '@/types';

export interface PollingResult {
  resultFile: FlowMedia;
  isVideo: boolean;
  isAudio: boolean;
  driveUid?: string;
  width?: number;
  height?: number;
}

const selector = (store: IAppStore) => ({
  setResultFile: store.setResultFile,
  setGenerationTimestamp: store.setGenerationTimestamp,
  updateContentFile: store.updateContentFile,
  incrementPendingReadyCount: store.incrementPendingReadyCount,
});

/**
 * Hook for handling generation polling and result processing
 */
const useGenerationPolling = () => {
  const {
    setResultFile,
    setGenerationTimestamp,
    updateContentFile,
    incrementPendingReadyCount,
  } = useAppStore(selector);

  const poll = useCallback(
    async ({
      generationId,
      placeholderUuid,
      presetTitle,
    }: {
      generationId: string;
      placeholderUuid: string;
      presetTitle: string;
    }): Promise<PollingResult> => {
      const pollUrl = `${MAGIC_FLOW_WEBHOOK_URL}/${generationId}/result`;
      const pollingResponse = await pollUntilCompleted({ url: pollUrl });

      const { resultFile, resultType, isVideo, isAudio } = buildFlowMedia({
        generationId,
        pollingResponse,
      });

      setResultFile(resultFile);
      const timestamp = Date.now();
      setGenerationTimestamp(timestamp);

      const { screen: currentScreen } = getAppStore();

      const { filename } = await renameDriveFile({
        driveUid: resultFile.driveUid,
        presetTitle,
        resultType,
        timestamp,
      });

      updateContentFile({
        uuid: placeholderUuid,
        updates: {
          uuid: resultFile.driveUid || placeholderUuid,
          url: resultFile.url,
          name: filename,
          type: resultFile.type,
          preview: {
            url: isVideo
              ? resultFile.poster
              : isAudio
              ? undefined
              : resultFile.url,
            width: resultFile.width,
            height: resultFile.height,
          },
          isLoading: false,
          generationStartTime: undefined,
          presetType: undefined,
        },
      });

      await clearGeneration(generationId);

      const decision = decidePostGenerationUX({ screen: currentScreen });

      if (decision.kind === 'notify') {
        // Surface completion via the "creations ready" toast (a tap takes the
        // user to the Creations grid). Never auto-open/navigate.
        incrementPendingReadyCount();
      }

      return {
        resultFile,
        isVideo,
        isAudio,
        driveUid: resultFile.driveUid,
        width: resultFile.width,
        height: resultFile.height,
      };
    },
    [
      setResultFile,
      setGenerationTimestamp,
      updateContentFile,
      incrementPendingReadyCount,
    ]
  );

  return { poll };
};

export default useGenerationPolling;
